/**
 * Practice Domain — Barrel Export
 *
 * Public API for the practice domain: session management, answer protocol,
 * validation schemas, and session validation utilities.
 *
 * Created in R31 (DDD Fase 4 — practice domain consolidation).
 * Cleaned up s36: removed cross-boundary re-exports to ../components/practice/.
 *
 * CL-1 Co-locatie (s62-s66):
 * - UI components migrating from components/practice/ → practice/components/
 * - Moved s63: PracticeErrorBoundary, QuestionErrorBoundary, PracticeHeaderMinimal,
 *   QuestionCardClean, SubSkillCheatSheet, CheatSheetDrawer, ColumnArithmetic, LongDivision
 * - Moved s64: feedback/ (11 files — FeedbackSplash optimized 667→420r, extracted feedbackSplashData.ts)
 * - Moved s65: SessionSummary (extracted sessionSummaryData.ts ~80r), MoneyAssembler (extracted moneyStyles.ts ~140r)
 *   Deleted 4 dead input files (NumericInput, TimeInput, DraggableList, MatchingPairs — 0 consumers)
 * - Moved s66: renderers/ batch 1 — types.ts, QuestionRendererFactory, ColumnArithmeticRenderer,
 *   LongDivisionRenderer, MoneyAssemblerRenderer, FillInRenderer, MultipleChoiceRenderer,
 *   TrueFalseSeriesRenderer, EstimationSliderRenderer (9 files)
 * - Moved s67: renderers/ batch 2 — BarChart, LineChart, PieChart (optimized: extracted
 *   chartDataUtils.ts + useChartSelection.ts, ~693r → ~395r + 2 shared modules),
 *   Clock, DragDropFill, RatioTable (6 renderers + 2 shared utils = 8 files)
 * - Moved s68: renderers/ batch 3 (FINAL) — Categorize, ErrorSpotting, Matching,
 *   MultiplicationGrid, NumberSequence, Ordering, Schedule, TableReading, TextHighlight
 *   (9 renderers). All 22 renderers + types + factory now co-located.
 * - Moved s69: PracticeSession (FINAL) — optimized 978→~680r, extracted 7 full-page
 *   states + exit modal → practiceSessionStates.tsx (~200r). CL-1 COMPLETE.
 * - Proxies at old locations for backward compatibility
 *
 * Architecture:
 * - hooks/usePracticeSession: Session state management
 * - logic/answerProtocol: Answer payload types + conversion utilities
 * - logic/answerPayload: Zod schemas for runtime validation
 * - logic/sessionValidation: Pre-flight session checks + integrity
 * - components/: Co-located UI (CL-1, incremental migration)
 */

// ── Session Hook ─────────────────────────────────────────────
export { usePracticeHandlers } from './hooks/usePracticeSession'

// ── Answer Protocol (types + conversion) ─────────────────────
export type {
  ChoicePayload,
  TextPayload,
  MappingPayload,
  OrderingPayload,
  CellSelectionPayload,
  KeyValuePayload,
  SegmentPayload,
  ValueSequencePayload,
  VisualDataPayload,
  TimePayload,
  TokenSetPayload,
  AnswerPayload,
  ValidationResult,
} from './logic/answerProtocol'
export {
  toAnswerPayload,
  fromAnswerPayload,
  parseAnswerPayload,
} from './logic/answerProtocol'

// ── Answer Payload Schemas (Zod runtime validation) ──────────
export {
  ChoicePayloadSchema,
  TextPayloadSchema,
  TimePayloadSchema,
  TokenSetPayloadSchema,
  MappingPayloadSchema,
  OrderingPayloadSchema,
  CellSelectionPayloadSchema,
  KeyValuePayloadSchema,
  SegmentPayloadSchema,
  ValueSequencePayloadSchema,
  VisualDataPayloadSchema,
  AnswerPayloadSchema,
  ValidationResultSchema,
  PayloadSchemaMap,
  validateAnswerPayload,
  validateValidationResult,
  validatePayloadByKind,
  isPayloadKind,
} from './logic/answerPayload'

// ── Session Validation ───────────────────────────────────────
export type {
  SessionValidationReport,
  SessionIntegritySnapshot,
} from './logic/sessionValidation'
export {
  validateSessionAvailability,
  logValidationReport,
  hasContentForGroup,
  createSessionSnapshot,
  validateSessionIntegrity,
} from './logic/sessionValidation'

// ── Question Time Baselines ──────────────────────────────────
export {
  getBaselineTimeMs,
  isFastResponse,
  normalizeResponseTime,
} from './logic/questionTimeBaselines'

// ── UI Components (co-located in CL-1) ──────────────────────
export { PracticeSession } from './components/PracticeSession'
export { PracticeErrorBoundary } from './components/PracticeErrorBoundary'
export { CheatSheetDrawer } from './components/CheatSheetDrawer'
export { QuestionCardClean } from './components/QuestionCardClean'
export { SessionSummary } from './components/SessionSummary'
export { PracticeHeaderMinimal } from './components/PracticeHeaderMinimal'
